import {SimulationRecord, SimulationTradeRecord} from "./TradeSimulationRecords";
import {ObjectUtil} from "../../js/util/ObjectUtil";
import {SimulationTrade} from "./SimulationTrade";
import {IsoDate} from "../../pseudo-types/DateTypes";

export class Simulation {

    private _name: string | null;
    private _underlying: string | null;
    private _initialBalance: number | null;
    private _activeDate: IsoDate | null;

    private constructor(public readonly data: SimulationRecord,
                        private readonly _trades: Array<SimulationTrade>) {
    }

    public get id(): number {
        return this.data.id;
    }

    public get name(): string {
        if (this._name == null) {
            return this.data.name;
        } else {
            return this._name;
        }
    }

    public set name(value: string) {
        this._name = value;
    }

    public get initialBalance(): number {
        if (this._initialBalance == null) {
            return this.data.initialBalance;
        } else {
            return this._initialBalance;
        }
    }

    public set initialBalance(value: number) {
        this._initialBalance = value;
    }

    public get activeDate(): IsoDate {
        if (this._activeDate == null) {
            return this.data.activeDate;
        } else {
            return this._activeDate;
        }
    }

    public set activeDate(value: IsoDate) {
        this._activeDate = value;
    }

    public get underlying(): string {
        if (this._underlying == null) {
            return this.data.underlying;
        } else {
            return this._underlying;
        }
    }

    public set underlying(value: string) {
        this._underlying = value;
    }

    public get isIndex() {
        return this._underlying === "XJO";
    }

    public get trades(): Array<SimulationTrade> {
        return [...this._trades];
    }

    public get hasChanges(): boolean {
        const nameChanged = this.name !== this.data.name;
        const underlyingChanged = this.underlying !== this.data.underlying;
        const initialBalanceChanged = this.initialBalance !== this.data.initialBalance;
        const tradesChanged = this.trades.some(t => t.hasChanges);
        const activeDateChanged = false;
        return nameChanged || underlyingChanged || initialBalanceChanged || tradesChanged || activeDateChanged;
    }

    public get hasTrades(): boolean {
        return this.trades.length > 0;
    }

    public addTrade(code: string, quantity: number, date: IsoDate, price: number): Simulation {
        const record: SimulationTradeRecord = {code, quantity, tradeDate: date, price};
        const trades = this.data.trades.concat(record);
        const data = ObjectUtil.merge({}, this.data, {trades});
        return Simulation.create(data);
    }

    public removeTrade(trade: SimulationTrade): Simulation {
        const newTrades = this.trades.filter(tr => tr !== trade);
        const records = newTrades.map(tr => tr.toRecord());
        const data = ObjectUtil.merge({}, this.data, {trades: null}, {trades: records});
        return Simulation.create(data);
    }

    public clear(): Simulation {
        const data = ObjectUtil.merge({}, this.data, {trades: null}, {trades: []});
        return Simulation.create(data);
    }

    public toRecord(): SimulationRecord {
        const trades = this.trades.map(t => t.toRecord());
        return {
            id: this.id,
            name: this.name,
            underlying: this.underlying,
            initialBalance: this.initialBalance,
            activeDate: this.activeDate,
            trades
        };
    }

    public copy(params: SimulationCopyParams): Simulation {
        return ObjectUtil.merge({}, this, params);
    }

    public static create(data: SimulationRecord): Simulation {
        const trades = data.trades.map(tr => SimulationTrade.from(tr));
        return new Simulation(data, trades);
    }
}

export type SimulationCopyParams = {
    [key in keyof Simulation]?: Simulation[key];
};
